import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
// icons
import { FiPlus } from 'react-icons/fi';

import { FormikProps } from 'formik';

import BasicButton from '../../../components/buttons/basicButton';
import AampeTooltip from '../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

import { getVariantTypeLabelsMap } from '../../../helpers/formulaHelpers';

import useComponentTypesQuery from '../../../hooks/component-types/useComponentTypesQuery';

// models
import { FormulaLabel } from '../../../models/formula.model';

import { FormulaCreateType } from '../FormulaCreateMainV2';
// components
import FormulaLabelsRow, { FormulaLabelFormRow } from './FormulaLabelsRow';

interface FormulaLabelsProps {
    formulaLabels: FormulaLabel[];
    formulaId?: number;
    createOrUpdateFormula?: FormulaCreateType;
    className?: string;
    formikProps?: FormikProps<any>;
    actionButtonStyle?: CSSProperties;
    customLabelStyle?: CSSProperties;
    variantTypePlaceholder?: string;
    hasWriteAccess: boolean;
}

const FormulaLabels: React.FC<React.PropsWithChildren<FormulaLabelsProps>> = ({
    formulaLabels,
    formulaId,
    createOrUpdateFormula,
    className,
    formikProps,
    actionButtonStyle,
    customLabelStyle,
    variantTypePlaceholder,
    hasWriteAccess,
}) => {
    const { data: componentTypes } = useComponentTypesQuery();
    const variantTypes = useMemo(
        () => componentTypes?.selectOptions ?? [],
        [componentTypes],
    );

    const [variantTypeOptions, setVariantTypeOptions] = useState(variantTypes);
    const [inputList, setInputList] = useState<FormulaLabelFormRow[]>([]);
    const variantTypeLabelsMap = useMemo(
        () => getVariantTypeLabelsMap(formulaLabels),
        [formulaLabels],
    );

    useEffect(() => {
        if (Object.keys(variantTypeLabelsMap).length) {
            setInputList(
                Object.keys(variantTypeLabelsMap).map((variantType) => ({
                    variant_type: {
                        label: componentTypes?.map[variantType].name ?? '',
                        value: variantType,
                    },
                    labels: variantTypeLabelsMap[variantType].map(
                        (label: FormulaLabel) => ({
                            value: label.label_id,
                            label: label.label_name,
                        }),
                    ),
                })),
            );
        } else {
            setInputList([
                {
                    variant_type: [] as any,
                    labels: [],
                },
            ]);
        }
    }, [componentTypes, variantTypeLabelsMap]);

    const handleAddRow = () => {
        setInputList([
            ...inputList,
            {
                variant_type: [] as any,
                labels: [],
            },
        ]);
        setVariantTypeOptions(
            variantTypeOptions.filter(
                (variantType) =>
                    !inputList.some(
                        (item) => variantType.value === item.variant_type.value,
                    ),
            ),
        );
    };

    return (
        <div className={className}>
            {inputList.map((item, index) => (
                <FormulaLabelsRow
                    key={item.variant_type.value || 'None'}
                    index={index}
                    rowData={item}
                    variantTypeOptions={variantTypeOptions}
                    inputList={inputList}
                    setInputList={setInputList}
                    formulaId={formulaId}
                    setVariantTypeOptions={setVariantTypeOptions}
                    createOrUpdateFormula={createOrUpdateFormula}
                    formikProps={formikProps}
                    customLabelStyle={customLabelStyle}
                    variantTypePlaceholder={variantTypePlaceholder}
                    isDisabled={!hasWriteAccess}
                />
            ))}
            {inputList.length !== variantTypes.length && hasWriteAccess && (
                <AampeTooltip
                    title="Attach additional personalization labels to the entire message group."
                    arrow
                    placement="right"
                >
                    <span style={actionButtonStyle}>
                        <StyledButton
                            name="Add label"
                            icon={<FiPlus size={16} />}
                            action={handleAddRow}
                            isCompact={true}
                            isPrimary={false}
                            isOutlined={false}
                            isDisabled={inputList.some(
                                (item) => !item.labels.length,
                            )}
                        />
                    </span>
                </AampeTooltip>
            )}
        </div>
    );
};

export default FormulaLabels;

const StyledButton = styled(BasicButton)({
    marginTop: aampeTheme.spacing(2),
    border: 'none',
    boxShadow: 'none',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '20px',
    '&:hover': {
        border: 'none',
    },
    '&:disabled': {
        border: 'none',
    },
});
